
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import CourseOverview from "@/components/courses/CourseOverview";
import CourseContent from "@/components/courses/CourseContent";
import EnrolledView from "@/components/courses/EnrolledView";
import UnenrolledView from "@/components/courses/UnenrolledView";
import RelatedCourses from "@/components/courses/RelatedCourses";
import { useCourseData } from "@/hooks/useCourseData";
import { usePurchaseNavigation } from "./course-detail/usePurchaseNavigation";
import { SectionLoader } from "./course-detail/CourseDetailSectionLoader";
import { CourseDetailInstructor } from "./course-detail/CourseDetailInstructor";
import { CourseDetailBenefits } from "./course-detail/CourseDetailBenefits";

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const {
    course,
    lessons,
    isCourseLoading,
    isLessonsLoading,
    courseError,
    lessonsError,
    isEnrolled,
    userCourse,
    isCheckingEnrollment,
    activeVideoId,
    activePdfUrl,
    activeLesson,
    handleLessonSelect,
    handleVideoEnd,
    handleLessonComplete,
  } = useCourseData(courseId, undefined);

  const handlePurchaseCourse = usePurchaseNavigation(course);

  if (isCourseLoading || isLessonsLoading || isCheckingEnrollment) {
    return <SectionLoader isLoading={true} error={null} />;
  }
  if (courseError || lessonsError || !course) {
    return <SectionLoader isLoading={false} error={courseError || lessonsError} />;
  }

  const modules = lessons.length > 0 ? 
    [
      {
        id: "module-1",
        title: course.title || "Course Module",
        lessons: lessons.map(lesson => ({
          id: lesson.id,
          title: lesson.title || "Lesson",
          duration: lesson.duration || course.duration || "30 min",
          video_id: lesson.video_id || "",
          isCompleted: (userCourse?.completed_lessons || []).includes(lesson.id),
          description: lesson.description || "Lesson content",
          pdf_url: lesson.pdf_url
        }))
      }
    ] : [
      {
        id: "module-1",
        title: course.title || "Course Module",
        lessons: [
          {
            id: "lesson-1",
            title: "Introduction",
            duration: course.duration || "30 min",
            video_id: "",
            isCompleted: false,
            description: "Introduction to the course",
            pdf_url: undefined
          }
        ]
      }
    ];
  const userProgress = isEnrolled && userCourse ?
     (userCourse.completed_lessons?.length / lessons.length) * 100
     : 0;
  
  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <CourseOverview 
        description={course.description || ""}
        benefits={[]} // handled by CourseDetailBenefits component below
      />
      <div className="mb-4">
        <CourseDetailBenefits />
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <CourseContent 
            modulesList={modules}
            isLoading={isLessonsLoading}
            error={lessonsError}
          />
          <CourseDetailInstructor />
        </div>
        <div className="lg:col-span-1">
          {isEnrolled ? (
            <EnrolledView 
              courseId={courseId || ""}
              course={{
                title: course.title || "",
                image_url: course.image_url || ""
              }}
              modulesList={modules}
              activeVideoId={activeVideoId}
              activePdfUrl={activePdfUrl}
              userProgress={userProgress}
              onLessonSelect={handleLessonSelect}
              onLessonComplete={handleLessonComplete}
              onVideoEnd={handleVideoEnd}
              activeLesson={activeLesson}
              userCourse={userCourse}
              setUserCourse={() => {}}
            />
          ) : (
            <UnenrolledView 
              course={course} 
              onPurchase={handlePurchaseCourse} 
            />
          )}
        </div>
      </div>
      <div className="mt-16">
        <RelatedCourses courses={[]} />
      </div>
    </div>
  );
};

export default CourseDetail;
